import { inject, Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { map, Observable, take } from 'rxjs';

import { ApprovalApplyOutcome, EMPTY_STATUS_CASCADE, StatusCascade } from '@efficiently/models';

import { ApprovalsRepository, SetApprovalStatusArgs } from '@efficiently/client-api-approvals';
import type { ApprovalApplyOutcome as GeneratedApprovalApplyOutcome } from '@efficiently/client-core-graphql-models';

import { SET_APPROVAL_STATUS, SET_APPROVAL_STATUS_WITH_CASCADE } from '../graphql/queries';

/**
 * The seam between the generated union and the `@efficiently/models` enum — two spellings of one
 * GraphQL enum. A total `Record` rather than a cast, so a member added or dropped on the schema side
 * is a build error where `as ApprovalApplyOutcome` would have waved it through.
 *
 * The enum side cannot be pinned this way: `tsconfig.ci.json` excludes `__tests__/**`, so nothing
 * typechecks a spec file. It is pinned at runtime in `__tests__/approvals-gql.repository.spec.ts`.
 */
const OUTCOME_BY_GENERATED: Record<GeneratedApprovalApplyOutcome, ApprovalApplyOutcome> = {
	Changed: ApprovalApplyOutcome.Changed,
	Unchanged: ApprovalApplyOutcome.Unchanged,
	NotApplied: ApprovalApplyOutcome.NotApplied,
};

@Injectable()
export class ApprovalsGqlRepository extends ApprovalsRepository {
	private readonly apollo = inject(Apollo);

	public override setApprovalStatus(args: SetApprovalStatusArgs[]): Observable<boolean> {
		return this.apollo
			.mutate({
				mutation: SET_APPROVAL_STATUS,
				variables: { args },
			})
			.pipe(
				map(({ data }) => data?.setApprovalStatus ?? false),
				take(1),
			);
	}

	public override setStatusWithCascade(args: SetApprovalStatusArgs[]): Observable<StatusCascade> {
		return this.apollo
			.mutate({
				mutation: SET_APPROVAL_STATUS_WITH_CASCADE,
				variables: { args },
			})
			.pipe(
				map(({ data }) => {
					const cascade = data?.setApprovalStatusWithCascade;

					if (!cascade) {
						return EMPTY_STATUS_CASCADE;
					}

					// TEMP-CODEGEN-FRONTIER(EFF-24658): these two are still read as the app-wide
					// `StatusCascade` domain shape.
					const { finishIds, finishes } = cascade as unknown as StatusCascade;

					// `rows` is read off the generated result, so dropping it from the document fails the
					// typecheck rather than passing silently under the cast above.
					const rows = cascade.rows?.map(row => ({
						_id: row._id,
						outcome: OUTCOME_BY_GENERATED[row.outcome],
					}));

					// Absent stays absent, never `[]`: a server that reports no outcomes is not one that
					// applied nothing, and callers branch on exactly that difference.
					if (!rows) {
						return { finishIds, finishes };
					}

					return { finishIds, finishes, rows };
				}),
				take(1),
			);
	}
}
